import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/common/StatusBadge";
import { AI, Brands, Reveal } from "./slideBits";
import {
  AlertTriangle, ArrowLeft, BatteryFull, Bell, Boxes, Camera, CalendarDays, Check, CheckCircle2, ChevronRight, ClipboardList, Home, MapPin, Mic, Navigation,
  Pause, Signal, Smartphone, Sparkles, Truck, Wifi, Wrench,
} from "lucide-react";
import "./felt.css";

/**
 * Slide «Feltvisning»: ein levande telefon som fortel historia i fire steg (Dagen din → Sjekkliste → Snakk inn avviket →
 * Registrert). Stega til venstre er synkroniserte med skjermen (skinna fyller seg i takt), klikk på eit steg hoppar dit,
 * klikk på telefonen pausar. Skjermane er demodata på bokmål (slik appen snakkar); teksten rundt er nynorsk.
 * Telefonen er 390 × 844 logiske px og blir skalert med transform – skarp tekst i alle storleikar.
 */

/* ---------- historia ---------- */
export const STEPS = [
  { id: "home", n: "01", tab: "home", dur: 5000, title: "Dagen din – klar når du låser opp", text: "Neste oppgåve, dagens transportar og prosjekt. Berre det som gjeld i dag." },
  { id: "task", n: "02", tab: "tasks", dur: 5800, title: "Sjekkliste og foto", text: "Kryss av, ta bilete, fullfør. Alt blir logga på prosjektet medan du jobbar." },
  { id: "voice", n: "03", tab: "issue", dur: 7800, title: "Snakk inn avviket", text: `Trykk på mikrofonen og fortel. ${AI} gjer tale til eit strukturert avvik – du kontrollerer og sender.` },
  { id: "done", n: "04", tab: "issue", dur: 5400, title: "Registrert – alle varsla", text: "Prosjektleiar, flåte og serviceordre – på under 30 sekund, utan skjema." },
];
/* kva telefonen utløyser på kontoret – éin chip per steg */
const CHIPS = {
  home: { side: "b", pos: { bottom: "22%" }, delay: 1.5, icon: Truck, title: "TR-2026-0921 · På veg", text: "6 modular · kran 11:00 · Mongstad" },
  task: { side: "a", pos: { top: "30%" }, delay: 3.7, icon: Camera, title: "2 bilete lagt til", text: "Modulrekke B · lagra på prosjektet" },
  voice: { side: "a", pos: { top: "19%" }, delay: 5.0, icon: Sparkles, title: `${AI} strukturerer`, text: "Tale → tittel, modul, alvor og kategori" },
  done: { side: "b", pos: { top: "15%" }, delay: 1.2, icon: Bell, title: "Prosjektleiar varsla", text: "AV-2026-0143 · serviceordre SV-0412 oppretta" },
};

/* ---------- demodata (bokmål) ---------- */
const CHECKLIST = [
  { id: "c1", text: "Sjekk fundamentblokker og nivå", done: true },
  { id: "c2", text: "Løft og plasser B-13 / B-14", done: true },
  { id: "c3", text: "Bolte sammen modulkoblinger", done: true },
  { id: "c4", text: "Montere skjøtebeslag tak", done: true },
  { id: "c5", text: "Koble el og VA mellom moduler", done: true },
  { id: "c6", text: "Tette og kontrollere taktekking", anim: 1500 },
  { id: "c7", text: "Montere trapp og rekkverk", anim: 3000 },
  { id: "c8", text: "Foto av ferdig rekke (4 bilder)" },
];
const TRANSCRIPT = "Vann renner inn ved vinduet på B-14 i andre etasje når det regner. Karmen er løs og må sikres før folk flytter inn i morgen.";
const WAVE = Array.from({ length: 26 }, (_, i) => ({ i, h: 0.35 + Math.abs(Math.sin(i * 1.7)) * 0.65, wd: 620 + ((i * 97) % 380), delay: -((i * 131) % 700) }));
const TIMELINE = [
  { icon: Bell, title: "Prosjektleder varslet", text: "Push og e-post · Kari Nilsen" },
  { icon: Wrench, title: "Serviceordre SV-0412 opprettet", text: "Tildelt flåte / vedlikehold" },
  { icon: Boxes, title: "B-14 merket «trenger tilsyn»", text: "Synlig i riggdesign og kapasitet" },
];

/* ---------- små byggesteinar ---------- */
const Sec = ({ icon: Icon, title, count, more = "Se alle" }) => (
  <div className="ph-sec"><span className="ph-row" style={{ gap: 7 }}><Icon className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />{title}<small>({count})</small></span>{more && <em>{more}</em>}</div>
);
const Back = () => <div className="ph-back"><ArrowLeft className="h-4 w-4" /> Tilbake</div>;

/* ---------- skjerm 1: Hjem ---------- */
function ScreenHome() {
  return (
    <div className="ph-body">
      <div className="ph-between" style={{ alignItems: "center" }}>
        <div><div className="ph-kicker">Fredag 2. oktober</div><div className="ph-h1">God morgen, Ola</div></div>
        <div className="ph-avatar">OH</div>
      </div>
      <div className="ph-hero ph-in" style={{ "--d": "500ms" }}>
        <div className="ph-hero-top"><span><i className="ph-live" /> Pågår nå</span><span className="ph-tabular">Frist 14:00</span></div>
        <div className="ph-hero-title">Montasje modulrekke B – etasje 2</div>
        <div className="ph-hero-sub">Mongstad Boligrigg · kran bestilt 11:00</div>
        <div className="ph-hero-foot">
          <div className="ph-bar ph-bar-light"><i style={{ "--p": 0.62 }} /></div>
          <span className="ph-tabular" style={{ fontSize: 12, opacity: 0.85 }}>5/8</span>
          <span className="ph-hero-btn">Fortsett <ChevronRight className="h-3.5 w-3.5" /></span>
        </div>
      </div>
      <div className="ph-stats">
        {[["Oppgaver", "3", "1 pågår"], ["Transport", "2", "i dag · kran"], ["Avvik", "1", "åpent"]].map(([l, v, s], i) => (
          <div key={l} className="ph-stat ph-in" style={{ "--d": `${700 + i * 90}ms` }}><div className="ph-stat-label">{l}</div><div className="ph-stat-value">{v}</div><div className="ph-stat-sub">{s}</div></div>
        ))}
      </div>
      <Sec icon={CalendarDays} title="I dag" count={3} />
      <div className="ph-card ph-task">
        <div className="ph-between"><div><div className="ph-task-title">Kontroll av brannvarsling, bygg A</div><div className="ph-task-meta">Mongstad Boligrigg · Inspeksjon</div></div><ChevronRight className="h-4 w-4 ph-muted" /></div>
        <div className="ph-badges"><StatusBadge status="ikke_startet" /><span className="ph-task-meta ph-tabular">Frist 16:00</span><span className="ph-task-meta ph-tabular" style={{ marginLeft: "auto" }}>0/6 punkter</span></div>
      </div>
      <div className="ph-card ph-task">
        <div className="ph-between"><div><div className="ph-task-title">Klargjøring sanitærmodul S-04</div><div className="ph-task-meta">Gismarvik Camp · Klargjøring</div></div><ChevronRight className="h-4 w-4 ph-muted" /></div>
        <div className="ph-badges"><StatusBadge status="ikke_startet" /><span className="ph-task-meta ph-tabular">Frist fre 3. okt</span></div>
      </div>
      <Sec icon={Truck} title="Transport" count={2} />
      <div className="ph-card ph-task">
        <div className="ph-between"><div><div className="ph-task-title">TR-2026-0921 · Utlevering</div><div className="ph-task-meta">Mongstad Boligrigg</div></div><StatusBadge status="pa_vei" /></div>
        <div className="ph-task-meta ph-row ph-tabular" style={{ gap: 12 }}><span>I dag 11:00–13:00</span><span>6 moduler</span><span>Kran</span></div>
      </div>
      <div className="ph-card ph-row" style={{ padding: "9px 12px", gap: 10 }}>
        <span className="ph-tl-dot" style={{ margin: 0, width: 30, height: 30 }}><MapPin className="h-3.5 w-3.5" /></span>
        <div style={{ minWidth: 0, flex: 1 }}><div className="ph-task-title" style={{ fontSize: 13.5 }}>Mongstad Boligrigg</div><div className="ph-task-meta">PRJ-2026-0012 · Mongstadvegen 1, Lindås</div></div>
        <Navigation className="h-4 w-4 ph-muted" />
      </div>
    </div>
  );
}

/* ---------- skjerm 2: Oppgåve med sjekkliste ---------- */
function ScreenTask() {
  return (
    <div className="ph-body">
      <Back />
      <div className="ph-card" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="ph-badges"><StatusBadge status="pagar" /><StatusBadge status="hoy" /><span className="ph-task-meta">Montasje</span></div>
        <div className="ph-h2">Montasje modulrekke B – etasje 2</div>
        <div className="ph-task-meta">Mongstad Boligrigg · Frist i dag 14:00 · 3,5 t brukt</div>
      </div>
      <div className="ph-card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="ph-between" style={{ alignItems: "center" }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>Sjekkliste</span>
          <span className="ph-task-meta ph-tabular"><span className="ph-swap" style={{ "--d": "3000ms" }}><span className="ph-swap-a">5</span><span className="ph-swap-b">7</span></span>/8</span>
        </div>
        <div className="ph-bar ph-bar-anim" style={{ flex: "0 0 auto" }}><i style={{ "--p0": 0.625, "--p1": 0.875, "--d": "3000ms" }} /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {CHECKLIST.map((c) => (
            <div key={c.id} className="ph-check" data-done={c.done ? "true" : "false"} data-anim={c.anim ? "true" : "false"} style={c.anim ? { "--d": `${c.anim}ms` } : undefined}>
              <span className="ph-check-box"><i><Check className="h-3 w-3" strokeWidth={3} /></i></span>
              <span className="ph-check-text">{c.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="ph-photos">
        <div className="ph-photo" />
        <div className="ph-photo ph-photo-2 ph-pop" style={{ "--d": "3600ms" }} />
        <div className="ph-photo-add"><Camera className="h-4 w-4" /> Ta bilde</div>
      </div>
      <div className="ph-actions">
        <div className="ph-btn"><Pause className="h-4 w-4" /> Pause</div>
        <div className="ph-btn ph-btn-primary"><CheckCircle2 className="h-4 w-4" /> Fullfør</div>
      </div>
    </div>
  );
}

/* ---------- skjerm 3: Snakk inn avviket (rec → think → done) ---------- */
function ScreenVoice({ live }) {
  const [phase, setPhase] = useState("rec");
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (!live) { setPhase("rec"); setSec(0); return undefined; }
    const t1 = setTimeout(() => setPhase("think"), 4500);
    const t2 = setTimeout(() => setPhase("done"), 5500);
    const tick = setInterval(() => setSec((s) => Math.min(s + 1, 9)), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearInterval(tick); };
  }, [live]);
  const words = TRANSCRIPT.split(" ");
  const filled = phase === "done";
  return (
    <div className="ph-body">
      <Back />
      <div><div className="ph-h2">Nytt avvik</div><div className="ph-kicker">Fortell hva som har skjedd – vi strukturerer.</div></div>
      <div className="ph-voice" data-phase={phase}>
        <div className="ph-voice-zone">
          <div className="ph-mic">
            <span className="ph-mic-ring" /><span className="ph-mic-ring" /><span className="ph-mic-ring" />
            {phase === "rec" && <Mic className="h-6 w-6" />}
            {phase === "think" && <Sparkles className="h-6 w-6 ph-spin" />}
            {phase === "done" && <Check className="h-6 w-6" strokeWidth={3} />}
          </div>
          <div className="ph-wave">{WAVE.map((b) => <i key={b.i} style={{ "--h": b.h, "--wd": `${b.wd}ms`, "--wdelay": `${b.delay}ms` }} />)}</div>
          <div className="ph-voice-label">
            {phase === "rec" && <><b style={{ width: 6, height: 6, borderRadius: 99, background: "#ff453a", display: "inline-block" }} /> Lytter · 0:0{sec} · trykk for å stoppe</>}
            {phase === "think" && <><Sparkles className="h-3.5 w-3.5" /> {AI} strukturerer …</>}
            {phase === "done" && <><CheckCircle2 className="h-3.5 w-3.5" /> Transkribert 0:09 · strukturert av {AI}</>}
          </div>
        </div>
        <div className="ph-transcript">
          {words.map((w, i) => <span key={i} className="ph-word" style={{ "--d": `${500 + i * 140}ms` }}>{w}&nbsp;</span>)}
        </div>
      </div>
      <div className="ph-field" data-filled={filled} style={{ "--d": "0ms" }}>
        <div className="ph-field-label">Tittel <span className="ph-ki">{AI}</span></div>
        <div className="ph-field-box"><span className="ph-skel" style={{ "--w": "62%" }} /><span className="ph-val">Vannlekkasje ved vindu, B-14</span></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div className="ph-field" data-filled={filled} style={{ "--d": "140ms" }}>
          <div className="ph-field-label">Modul <span className="ph-ki">{AI}</span></div>
          <div className="ph-field-box"><span className="ph-skel" style={{ "--w": "50%" }} /><span className="ph-val">B-14 · B24</span></div>
        </div>
        <div className="ph-field" data-filled={filled} style={{ "--d": "220ms" }}>
          <div className="ph-field-label">Kategori <span className="ph-ki">{AI}</span></div>
          <div className="ph-field-box"><span className="ph-skel" style={{ "--w": "40%" }} /><span className="ph-val">Skade · tetthet</span></div>
        </div>
      </div>
      <div className="ph-field" data-filled={filled} style={{ "--d": "320ms" }}>
        <div className="ph-field-label">Alvorlighet <span className="ph-ki">{AI}</span></div>
        <div className="ph-seg" data-filled={filled} style={{ "--d": "320ms" }}>{["Lav", "Middels", "Høy", "Kritisk"].map((s) => <span key={s} data-on={s === "Høy" ? "true" : "false"}>{s}</span>)}</div>
      </div>
      <div className="ph-cta" data-armed={filled}><AlertTriangle className="h-4 w-4" /> Send avvik</div>
    </div>
  );
}

/* ---------- skjerm 4: Kvittering ---------- */
function ScreenDone() {
  return (
    <div className="ph-body">
      <div className="ph-card ph-done">
        <svg viewBox="0 0 64 64" className="ph-check-svg" aria-hidden><circle cx="32" cy="32" r="29" className="ph-check-ring" /><path d="M20 33 l8 8 l16 -17" className="ph-check-mark" /></svg>
        <div className="ph-h2">Avvik registrert</div>
        <div className="ph-ref">Referanse <b>AV-2026-0143</b></div>
        <div className="ph-kicker" style={{ maxWidth: 280 }}>Vannlekkasje ved vindu, B-14 · 2 bilder. Alt under skjedde automatisk.</div>
      </div>
      <div className="ph-card" style={{ paddingTop: 6, paddingBottom: 6 }}>
        <div className="ph-timeline">
          {TIMELINE.map((t, i) => (
            <div key={t.title} className="ph-tl ph-in" style={{ "--d": `${1100 + i * 420}ms` }}>
              <span className="ph-tl-dot"><t.icon className="h-3 w-3" /></span>
              <div><div className="ph-tl-title">{t.title}</div><div className="ph-tl-text">{t.text}</div></div>
            </div>
          ))}
        </div>
      </div>
      <div className="ph-card ph-task ph-in" style={{ "--d": "2500ms" }}>
        <div className="ph-between"><div><div className="ph-task-title">Vannlekkasje ved vindu, B-14</div><div className="ph-task-meta">AV-2026-0143 · nå</div></div><div className="ph-row" style={{ gap: 4 }}><StatusBadge status="hoy" /><StatusBadge status="apen" /></div></div>
      </div>
      <div className="ph-actions">
        <div className="ph-btn">Nytt avvik</div>
        <div className="ph-btn ph-btn-primary"><Home className="h-4 w-4" /> Til hjem</div>
      </div>
    </div>
  );
}

export const SCREENS = { home: ScreenHome, task: ScreenTask, voice: ScreenVoice, done: ScreenDone };
export const TABS = [
  { id: "home", label: "Hjem", icon: Home },
  { id: "tasks", label: "Oppgaver", icon: ClipboardList },
  { id: "transport", label: "Transport", icon: Truck },
  { id: "issue", label: "Nytt avvik", icon: AlertTriangle, accent: true },
];

/* ---------- telefonen ---------- */
const PHONE_W = 390, PHONE_H = 844, FRAME = 12;
function usePhoneScale(ref) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const thumb = !!el.closest(".pres-thumb-scale");
    const fit = () => {
      const w = el.clientWidth;
      let h = el.clientHeight;
      // på den levande sliden må figuren òg få plass under bretten – elles veks figuren med telefonen og sliden rullar
      if (!thumb && window.innerWidth >= 1024) h = Math.min(h, window.innerHeight - el.getBoundingClientRect().top - 64);
      if (!w || !h) return;
      setScale(Math.max(0.3, Math.min(1, (h - 8) / (PHONE_H + FRAME * 2), (w - 8) / (PHONE_W + FRAME * 2))));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener("resize", fit);
    return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
  }, [ref]);
  return scale;
}

export function PhoneMock({ index, prevIndex, className }) {
  const step = STEPS[index];
  return (
    <div className={cn("pres-phone", className)} style={{ width: PHONE_W + FRAME * 2, height: PHONE_H + FRAME * 2 }} data-testid="pres-phone" data-screen={step.id}>
      <span className="pres-phone-btn pres-phone-btn-mute" /><span className="pres-phone-btn pres-phone-btn-volup" /><span className="pres-phone-btn pres-phone-btn-voldown" /><span className="pres-phone-btn pres-phone-btn-power" />
      <div className="pres-phone-screen" style={{ width: PHONE_W, height: PHONE_H }}>
        <div className="ph-status">
          <span className="ph-time">09:41</span>
          <span className="ph-island">
            <span className="ph-island-rec" data-on={step.id === "voice" ? "true" : "false"} data-testid="pres-phone-island-rec"><b /><span className="ph-island-bars">{[0, 1, 2, 3].map((i) => <i key={i} style={{ "--i": i }} />)}</span></span>
          </span>
          <span className="ph-status-icons"><Signal className="h-3.5 w-3.5" /><Wifi className="h-3.5 w-3.5" /><BatteryFull className="h-4 w-4" /></span>
        </div>
        <div className="ph-pages">
          {STEPS.map((s, i) => {
            const Screen = SCREENS[s.id];
            const pos = i === index ? "cur" : i < index ? "prev" : "next";
            // sider som låg bak (prev) og no skal «fram att» til høgre (pop til rot) hoppar utan overgang
            const jump = pos === "next" && prevIndex != null && i < prevIndex;
            return (
              <div key={s.id} className="ph-page" data-pos={pos} data-jump={jump ? "true" : "false"} aria-hidden={pos !== "cur"} data-testid={`pres-phone-page-${s.id}`}>
                <Screen live={pos === "cur"} />
              </div>
            );
          })}
        </div>
        <div className="ph-tabbar">
          <div className="ph-tabs">
            {TABS.map((t) => <div key={t.id} className="ph-tab" data-on={t.id === step.tab ? "true" : "false"} data-accent={t.accent ? "true" : "false"}><span className="ph-tab-ico"><t.icon className="h-5 w-5" /></span>{t.label}</div>)}
          </div>
          <div className="ph-home" />
        </div>
        <span className="pres-phone-glare" />
      </div>
    </div>
  );
}

/* ---------- sliden ---------- */
export default function FeltSlide() {
  const [state, setState] = useState({ index: 0, cycle: 0, prev: null });
  const [paused, setPaused] = useState(false);
  const figRef = useRef(null);
  const scale = usePhoneScale(figRef);
  const [thumb, setThumb] = useState(false);
  useEffect(() => { setThumb(!!figRef.current?.closest(".pres-thumb-scale")); }, []);
  useEffect(() => {
    if (paused || thumb) return undefined;
    const t = setTimeout(() => setState((s) => ({ index: (s.index + 1) % STEPS.length, cycle: s.cycle + 1, prev: s.index })), STEPS[state.index].dur);
    return () => clearTimeout(t);
  }, [state, paused, thumb]);
  const go = (i) => setState((s) => ({ index: i, cycle: s.cycle + 1, prev: s.index }));
  const togglePause = () => setPaused((p) => { if (p) setState((s) => ({ ...s, cycle: s.cycle + 1 })); return !p; });
  const step = STEPS[state.index];
  const chip = CHIPS[step.id];
  const url = typeof window !== "undefined" ? `${window.location.origin}/felt` : "/felt";
  const W = (PHONE_W + FRAME * 2) * scale, H = (PHONE_H + FRAME * 2) * scale;
  return (
    <div className="pres-slide-inner flex flex-col" data-testid="pres-slide-felt">
      <Brands />
      <div className="pres-felt" data-paused={paused ? "true" : "false"} data-step={step.id}>
        <div className="pres-felt-copy">
          <h2 className="pres-h2 pres-felt-title" data-testid="pres-felt-title">
            <Reveal i={0}>Heile arbeidsdagen</Reveal>
            <Reveal i={1}>i lomma til montøren</Reveal>
          </h2>
          <p className="pres-lead pres-felt-lead pres-in" style={{ "--i": 2 }}>Same system, tilpassa byggjeplassen. Du kan til og med snakke til det.</p>
          <ol className="pres-felt-steps" data-testid="pres-felt-steps" data-pres-hold>
            {STEPS.map((s, i) => {
              const active = i === state.index;
              return (
                <li key={s.id} className="contents">
                  <button type="button" className="pres-felt-step pres-in" style={{ "--i": 4 + i }} data-active={active ? "true" : "false"} aria-current={active ? "step" : undefined} onClick={() => go(i)} data-testid={`pres-felt-step-${s.id}`}>
                    {active && <span key={`${s.id}-${state.cycle}`} className="pres-felt-step-fill" style={{ "--dur": `${s.dur}ms` }} aria-hidden />}
                    <span className="pres-felt-step-n">{s.n}</span>
                    <span><span className="pres-felt-step-title" style={{ display: "block" }}>{s.title}</span><span className="pres-felt-step-text" style={{ display: "block" }}>{s.text}</span></span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="pres-felt-qr pres-in" style={{ "--i": 9 }} data-testid="pres-felt-qr" data-pres-hold>
            <div className="pres-felt-qr-code"><QRCodeSVG value={url} size={56} level="M" bgColor="transparent" fgColor="#0B1220" /></div>
            <div className="min-w-0">
              <div className="pres-felt-qr-title"><Smartphone className="h-4 w-4" /> Prøv sjølv – skann</div>
              <div className="pres-felt-qr-text">Logg inn med feltbrukaren.</div>
              <div className="pres-felt-qr-url" data-testid="pres-felt-url">{url.replace(/^https?:\/\//, "")}</div>
            </div>
          </div>
        </div>
        <div className="pres-felt-figure" ref={figRef} data-pres-hold>
          <div className="pres-felt-stage pres-pop" style={{ "--i": 3, width: W, height: H }}>
            <div className="pres-felt-glow" />
            <div className="pres-felt-floor" />
            <div className="pres-felt-float">
              <div className="pres-phone-scale" style={{ transform: `scale(${scale})` }} onClick={togglePause} role="button" aria-label={paused ? "Spel av" : "Pause"} data-testid="pres-phone-toggle">
                <PhoneMock index={state.index} prevIndex={state.prev} />
              </div>
            </div>
            <AnimatePresence>
              {chip && !thumb && (
                <motion.div
                  key={`${step.id}-${state.cycle}`}
                  className={cn("pres-felt-chip", chip.side === "a" ? "pres-felt-chip-a" : "pres-felt-chip-b")}
                  style={chip.pos}
                  initial={{ opacity: 0, y: 10, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, scale: 1, transition: { delay: chip.delay, duration: 0.5, ease: [0.34, 1.3, 0.64, 1] } }}
                  exit={{ opacity: 0, y: -6, scale: 0.96, transition: { duration: 0.22 } }}
                  data-testid={`pres-felt-chip-${step.id}`}
                >
                  <span className="pres-felt-chip-icon"><chip.icon className="h-4 w-4" /></span>
                  <div><div className="pres-felt-chip-title">{chip.title}</div><div className="pres-felt-chip-text">{chip.text}</div></div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="pres-felt-pause" aria-live="polite"><Pause className="h-3 w-3" /> Pause</div>
          </div>
        </div>
      </div>
    </div>
  );
}

